// Manifest resolver (ARCHITECTURE.md §7.3).
//
// Reads package manifests and extracts dependency relationships as GraphEdges.
// Manifest edges link the project (the manifest file) to external packages.
// They are always `deterministic` and carry `manifest_dependency` evidence.
//
// Only `package.json` is supported in this phase; `Cargo.toml` and
// `pyproject.toml` (§7.3) are future work and intentionally left unhandled.

import type { GraphEdge } from "../types.js";

/**
 * The package.json dependency sections we extract edges from, in a fixed order
 * so output is deterministic. The section name doubles as the evidence detail
 * prefix (e.g. "dependencies.react").
 */
const PACKAGE_JSON_SECTIONS = [
  "dependencies",
  "devDependencies",
  "peerDependencies",
] as const;

/** True when `value` is a plain object (a dependency-spec record). */
function isDependencyRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Resolve dependency edges from a parsed manifest.
 *
 * @param manifestPath Path to the manifest file (used as the edge `from`).
 * @param content      Parsed manifest object (e.g. `JSON.parse(package.json)`).
 * @returns            One `depends_on` GraphEdge per dependency. Non-package.json
 *                     manifests and missing/malformed sections yield `[]`.
 */
export function resolveManifestEdges(
  manifestPath: string,
  content: Record<string, unknown>,
): GraphEdge[] {
  // Only package.json is supported for now (§7.3).
  if (!manifestPath.endsWith("package.json")) return [];

  const edges: GraphEdge[] = [];

  for (const section of PACKAGE_JSON_SECTIONS) {
    const deps = content[section];
    if (!isDependencyRecord(deps)) continue;

    for (const packageName of Object.keys(deps)) {
      edges.push({
        from: manifestPath,
        to: packageName,
        kind: "depends_on",
        classification: "deterministic",
        evidence: [
          {
            type: "manifest_dependency",
            detail: `${section}.${packageName}`,
            confidence: 1.0,
          },
        ],
      });
    }
  }

  return edges;
}
